import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { User, Pencil, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ProfileFields } from "@/components/crew/ProfileFields";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("crewToken")}` },
  withCredentials: true,
});

export const ProfileSheet = () => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEditing(false);
    axios.get(`${API}/crew/me`, authConfig())
      .then(({ data }) => { setProfile(data); setForm(data); })
      .catch(() => toast.error("Could not load your profile"));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/crew/me`, form, authConfig());
      setProfile(data);
      setForm(data);
      setEditing(false);
      const stored = JSON.parse(localStorage.getItem("crewData") || "{}");
      localStorage.setItem("crewData", JSON.stringify({
        ...stored, name: `${data.first_name} ${data.last_name}`, preferred_name: data.preferred_name,
      }));
      toast.success("Your info has been updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          aria-label="My profile"
          data-testid="nav-profile-btn"
          className="w-10 h-10 flex items-center justify-center rounded-sm text-white hover:text-crew-yellow hover:bg-zinc-800 transition-colors"
        >
          <User className="w-7 h-7" fill="currentColor" strokeWidth={1.5} />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-crew-zinc border-l border-zinc-800 text-white w-full sm:max-w-lg overflow-y-auto" data-testid="profile-sheet">
        <SheetHeader className="text-left mb-6">
          <p className="text-crew-yellow text-xs font-bold uppercase tracking-[0.25em]">My Application</p>
          <SheetTitle className="text-3xl font-black text-white tracking-tighter" data-testid="profile-name">
            {profile ? `${profile.first_name} ${profile.last_name}` : "Loading…"}
          </SheetTitle>
          <SheetDescription className="text-zinc-400">Keep your details up to date so we can reach you.</SheetDescription>
        </SheetHeader>

        {!profile ? (
          <Loader2 className="w-6 h-6 animate-spin text-crew-yellow" data-testid="profile-loading" />
        ) : (
          <>
            <ProfileFields form={form} setForm={setForm} editing={editing} />
            <div className="flex gap-3 mt-8 sticky bottom-0 bg-crew-zinc py-4 border-t border-zinc-800">
              {editing ? (
                <>
                  <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="bg-crew-yellow text-black hover:bg-white font-bold uppercase rounded-sm flex-1">
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                  <Button variant="outline" onClick={() => { setForm(profile); setEditing(false); }} data-testid="profile-cancel-btn" className="border-zinc-700 bg-transparent text-white hover:bg-zinc-800 rounded-sm">
                    Cancel
                  </Button>
                </>
              ) : (
                <Button onClick={() => setEditing(true)} data-testid="profile-edit-btn" className="bg-crew-yellow text-black hover:bg-white font-bold uppercase rounded-sm flex-1">
                  <Pencil className="w-4 h-4 mr-2" /> Edit My Info
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
